import React, { useState, useEffect } from 'react';
import Modal from '../ui/Modal';
import { User } from '../../types/auth';
import { userService, CreateUserRequest, UpdateUserRequest } from '../../services/userService';
import { useAuth } from '../../contexts/AuthContext';
import { PASSWORD_MIN_LENGTH, PASSWORD_LABEL_SUFFIX } from '../../lib/passwordPolicy';

interface UserFormProps {
  user: User | null; // null = création, User = édition
  onClose: () => void;
  onSave: () => void;
}

export const UserForm: React.FC<UserFormProps> = ({ user, onClose, onSave }) => {
  const isEdit = !!user;
  const { user: currentUser } = useAuth();
  const isSelf = isEdit && user?.id === currentUser?.id;

  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [username, setUsername] = useState('');
  const [role, setRole] = useState<'regular' | 'superadmin'>('regular');
  const [password, setPassword] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (user) {
      setFullName(user.full_name || '');
      setEmail(user.email);
      setUsername(user.username);
      setRole(user.system_role);
    }
  }, [user]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSaving(true);

    try {
      if (isEdit) {
        const updateData: UpdateUserRequest = {
          full_name: fullName || undefined,
          email,
          ...(isSelf ? {} : { system_role: role }),
        };
        await userService.updateUser(user.id, updateData);
      } else {
        if (!password) {
          setError('Le mot de passe est obligatoire');
          setSaving(false);
          return;
        }
        if (!username) {
          setError("Le nom d'utilisateur est obligatoire");
          setSaving(false);
          return;
        }
        const createData: CreateUserRequest = {
          full_name: fullName || undefined,
          email,
          username,
          system_role: role,
          password,
        };
        await userService.createUser(createData);
      }
      onSave();
    } catch (err: any) {
      setError(err.message || 'Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      isOpen={true}
      onClose={onClose}
      title={isEdit ? 'Modifier l\'utilisateur' : 'Créer un utilisateur'}
      size="sm"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        {error && (
          <div className="bg-red-50 text-red-600 p-3 rounded text-sm">
            {error}
          </div>
        )}

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Nom complet</label>
          <input
            type="text"
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            placeholder="Jean Dupont"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            placeholder="jean@exemple.com"
          />
        </div>

        {!isEdit && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Nom d'utilisateur *</label>
            <input
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
              className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              placeholder="jdupont"
            />
          </div>
        )}

        {!isSelf && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Rôle *</label>
            <select
              value={role}
              onChange={(e) => setRole(e.target.value as 'regular' | 'superadmin')}
              className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="regular">Utilisateur</option>
              <option value="superadmin">Superadmin</option>
            </select>
          </div>
        )}

        {!isEdit && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Mot de passe * {PASSWORD_LABEL_SUFFIX}</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={PASSWORD_MIN_LENGTH}
              className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              placeholder="••••••••"
            />
          </div>
        )}

        <div className="flex justify-end gap-3 pt-4 border-t">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded"
          >
            Annuler
          </button>
          <button
            type="submit"
            disabled={saving}
            className="px-4 py-2 text-sm bg-primary text-white rounded hover:bg-primary-600 disabled:opacity-50"
          >
            {saving ? 'Enregistrement...' : isEdit ? 'Modifier' : 'Créer'}
          </button>
        </div>
      </form>
    </Modal>
  );
};
